import type { CardCopTopUpRates } from "./cardCopTopUp.types";

/**
 * Tipos de transacción según la API. Const objects como alternativa a enum (compatible con
 * erasableSyntaxOnly).
 */
export const TransactionType = {
  PURCHASE: "PURCHASE",
  TOPUP: "TOPUP",
  REFUND: "REFUND",
  ATM_WITHDRAWAL: "ATM_WITHDRAWAL",
  WITHDRAWAL: "WITHDRAWAL",
  FEE: "FEE",
  TRANSFER: "TRANSFER",
  DEPOSIT: "DEPOSIT",
  REVERSAL: "REVERSAL",
  ADJUSTMENT: "ADJUSTMENT",
} as const;

export type TransactionType = (typeof TransactionType)[keyof typeof TransactionType];

export const OperationType = {
  DEBIT: "DEBIT",
  CREDIT: "CREDIT",
} as const;

export type OperationType = (typeof OperationType)[keyof typeof OperationType];

export const TransactionStatus = {
  PENDING: "PENDING",
  COMPLETED: "COMPLETED",
  FAILED: "FAILED",
  REVERSED: "REVERSED",
} as const;

export type TransactionStatus = (typeof TransactionStatus)[keyof typeof TransactionStatus];

/**
 * Phase of a purchase the issuer reports in two steps — first the authorization, then the settlement at the
 * final FX rate — each as its own movement. `AUTHORIZATION` is the one shown; `SETTLEMENT` is the echo of
 * that same purchase and is hidden.
 *
 * `null` means "this movement is not part of a two-phase purchase" (top-ups, withdrawals, declines,
 * reversals) and must never be read as a phase. Only the explicit `SETTLEMENT` value is filtered out.
 */
export const SettlementStage = {
  AUTHORIZATION: "AUTHORIZATION",
  SETTLEMENT: "SETTLEMENT",
} as const;

export type SettlementStage = (typeof SettlementStage)[keyof typeof SettlementStage];

/** Ubicación asociada a una transacción de tarjeta. */
export interface TransactionLocation {
  city?: string | null;
  country?: string | null;
  state?: string | null;
  postal_code?: string | null;
  address?: string | null;
  latitude?: number | null;
  longitude?: number | null;
}

/**
 * User who performed the movement (traceability). The internal endpoint returns only name and email — no
 * other user data.
 */
export interface TransactionCreatedBy {
  full_name: string;
  email: string;
}

export interface CardTransactionCopTopUp extends CardCopTopUpRates {
  id: string;
  /** COP debited from the wallet. */
  send_amount: string | null;
  /** USDT bought and sent to the card. */
  deliver_amount: string | null;
  /** USD the card credited; the expected credit until it does. */
  credited_amount: string | null;
  deposit_fee_percent: string | null;
  deposit_fee_amount: string | null;
}

/** Transacción de tarjeta (movimiento) según la API. */
export interface CardTransaction {
  id: string;
  card_id: string;
  amount: number;
  currency: string;
  usd_amount: number | null;
  exchange_rate: number | null;
  spot_variation_percentage: number | null;
  installments: number | null;
  transaction_type: TransactionType;
  operation_type: OperationType | null;
  status: TransactionStatus;
  settlement_stage: SettlementStage | null;
  description: string | null;
  merchant_name: string | null;
  merchant_category_code: string | null;
  merchant_id: string | null;
  merchant_terminal_id: string | null;
  location: TransactionLocation | null;
  provider_transaction_id: string;
  original_transaction_id: string | null;
  provider_metadata_id: string | null;
  metadata: Record<string, unknown> | null;
  created_by?: TransactionCreatedBy | null;
  /** `COP_BALANCE` on a deposit a top-up from the organization's COP wallet produced. */
  top_up_source?: "COP_BALANCE" | null;
  /** On such a deposit (detail only): what was paid, bought and credited, and the rate it came to. */
  cop_top_up?: CardTransactionCopTopUp | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

/**
 * Respuesta paginada de la API de transacciones/movimientos de tarjeta. Coincide con el payload: { data,
 * total, skip, limit, hasMore }
 */
export interface CardTransactionsResponse {
  data: CardTransaction[];
  total: number;
  skip: number;
  limit: number;
  hasMore: boolean;
}
